import { opinDrift } from '../catalog/catalog-data.ts';
import { filterLabel, statusLabel } from '../ui/labels.ts';
import { StatusBadge } from '@finnest/ui';
import type { StatusTone } from '@finnest/ui';
import type {
  GovernanceRow,
  GovernanceView,
  PhaseFilter,
  SurfaceFilter,
} from '../contract/governance-model-types.ts';
import type {
  AdminDataScope,
  OpinProbeDefinition,
  OpinProbeRequest,
  OpinProbeResult,
  OpinProbeScenario,
} from '@finnest/power-admin-contracts';

import { Schema } from 'effect';

export type { GovernanceView, OpinProbeScenario, PhaseFilter, SurfaceFilter };

/** The OPIN governance sections Conformidade shows; a Conformidade link names one as `?secao=`. */
export const OpinWorkspaceTab = Schema.Literals(['overview', 'catalog']);
export type OpinWorkspaceTab = typeof OpinWorkspaceTab.Type;

/** The section Conformidade opens on when its link names none. */
export const DEFAULT_WORKSPACE_TAB = 'overview' satisfies OpinWorkspaceTab;

/**
 * What a governance screen renders: the endpoint tests, or Conformidade with the workspace section
 * its link names and how it asks for another one. A screen with no sections reads only `section`.
 */
export type GovernanceSectionProps =
  | { readonly section: 'testes' }
  | {
      readonly section: 'conformidade';
      readonly workspaceTab: OpinWorkspaceTab;
      readonly onWorkspaceTab: (tab: OpinWorkspaceTab) => void;
    };

export const ATTENTION_ROW_LIMIT = 6;

export type ProbeServerState =
  | { readonly kind: 'local'; readonly source: 'browser' }
  | { readonly kind: 'server'; readonly result: OpinProbeResult };

export type ProbePresentation = {
  method: string;
  methodClass: string;
  path: string;
  scalarPath: string;
  scopes: string;
  permissions: string;
};

export const SOURCE_MODE_IS_LIVE = {
  live: true,
  artifact: false,
  demo: false,
} as const satisfies Record<AdminDataScope['sourceMode'], boolean>;

/** The source of the data the screen reads, as the operator names it. */
export const SOURCE_MODE_LABEL = {
  live: 'ao vivo',
  artifact: 'artefato registrado',
  demo: 'demonstração',
} as const satisfies Record<AdminDataScope['sourceMode'], string>;

export const INITIAL_EXECUTOR_MESSAGE = {
  live: 'Executando o teste no serviço Power…',
  artifact: 'Execução ao vivo desligada para a origem de dados escolhida.',
  demo: 'Execução ao vivo desligada para a origem de dados escolhida.',
} as const satisfies Record<AdminDataScope['sourceMode'], string>;

const PENDING_PROBE_DETAIL = {
  'not-run': 'Aguardando o serviço Power executar esta verificação.',
  passed: 'O serviço Power está executando esta verificação.',
  failed: 'O serviço Power está executando esta verificação.',
  warning: 'O serviço Power está executando esta verificação.',
  'not-configured': 'O serviço Power está executando esta verificação.',
} as const satisfies Record<OpinProbeResult['status'], string>;

export const PROBE_RUN_MESSAGE = {
  failed:
    'Os testes do serviço Power retornaram um erro. Verifique se o serviço está no ar e o endereço da API.',
  passed: 'O serviço Power respondeu com as evidências, sem dados sensíveis.',
  warning: 'O serviço Power respondeu com as evidências, sem dados sensíveis.',
  'not-configured': 'O serviço Power respondeu com as evidências, sem dados sensíveis.',
  'not-run': 'O serviço Power respondeu com as evidências, sem dados sensíveis.',
} as const satisfies Record<OpinProbeResult['status'], string>;

const LIVE_PENDING_STATUS = {
  running: 'not-run',
  idle: 'failed',
} as const satisfies Record<'idle' | 'running', OpinProbeResult['status']>;

const SUCCESS_TONES = ['clear', 'implemented', 'passed', 'ready', 'seeded', 'supported'];
const DANGER_TONES = ['missing', 'failed', 'error'];
const WARNING_TONES = ['partial', 'variant', 'version-variant', 'warning'];
const ATTENTION_MOCK_STATUSES = ['missing', 'variant', 'version-variant'];

const toPendingProbeResult = (
  result: OpinProbeResult,
  status: OpinProbeResult['status'],
): OpinProbeResult => ({
  ...result,
  status,
  checks: result.checks.map((check) => ({
    ...check,
    status,
    detail: PENDING_PROBE_DETAIL[status],
  })),
  notes: [
    'O navegador mostra as evidências ao vivo depois que o serviço Power conclui a chamada.',
    'Um resultado de exemplo nunca aparece como evidência de produção.',
  ],
});

const localDisplayedResult = (
  request: OpinProbeRequest,
  localResult: OpinProbeResult,
  isRunning: boolean,
): OpinProbeResult => {
  const pendingStatus = LIVE_PENDING_STATUS[isRunning ? 'running' : 'idle'];
  const bySource = {
    live: toPendingProbeResult(localResult, pendingStatus),
    artifact: localResult,
    demo: localResult,
  } as const satisfies Record<OpinProbeRequest['sourceMode'], OpinProbeResult>;
  return bySource[request.sourceMode];
};

export const displayedProbeResult = (
  serverState: ProbeServerState,
  request: OpinProbeRequest,
  localResult: OpinProbeResult,
  isRunning: boolean,
): OpinProbeResult => {
  const local = localDisplayedResult(request, localResult, isRunning);
  const byKind = {
    server: serverState.kind === 'server' ? serverState.result : local,
    local,
  } satisfies Record<ProbeServerState['kind'], OpinProbeResult>;
  return byKind[serverState.kind];
};

export const countBySeverity = (severity: 'error' | 'warning'): number =>
  opinDrift.issues.filter((issue) => issue.severity === severity).length;

export const statusTone = (status: string): StatusTone => {
  if (SUCCESS_TONES.includes(status)) return 'success';
  if (DANGER_TONES.includes(status)) return 'danger';
  if (WARNING_TONES.includes(status)) return 'warning';
  return 'neutral';
};

const scenarioLabels = {
  'provider-demo-allowed': 'dados de exemplo, acesso permitido',
  'provider-demo-empty': 'dados de exemplo, sem registros',
  'missing-consent': 'consentimento ausente',
  'invalid-scope': 'escopo inválido',
} as const satisfies Record<OpinProbeScenario, string>;

export const scenarioLabel = (scenario: OpinProbeScenario): string => scenarioLabels[scenario];

export const rowNeedsAttention = ({ conformance, mockOpin, status }: GovernanceRow): boolean =>
  status !== 'implemented' ||
  conformance.result !== 'passed' ||
  ATTENTION_MOCK_STATUSES.includes(mockOpin.status);

export const rowKey = ({ family, surface, target }: GovernanceRow): string =>
  `${surface}-${family}-${target}`;

export const rowStatus = ({ status }: GovernanceRow): string => status;

export const rowSurfaceTarget = ({ surface, target }: GovernanceRow): string =>
  `${filterLabel(surface)} · ${target}`;

export const probePresentation = ({
  method,
  path,
  requiredPermissions,
  requiredScopes,
  scalarPath,
}: OpinProbeDefinition): ProbePresentation => ({
  method,
  methodClass: `opin-method opin-method-${method.toLowerCase()}`,
  path,
  scalarPath,
  scopes: requiredScopes.join(', ') || 'nenhum',
  permissions: requiredPermissions.join(', ') || 'nenhum',
});

export const FilterBar = <T extends string>({
  active,
  label,
  onSelect,
  options,
}: {
  active: T;
  label: string;
  onSelect: (value: T) => void;
  options: readonly T[];
}): JSX.Element => (
  <nav className="opin-filters" aria-label={label}>
    {options.map((option) => (
      <button
        className={option === active ? 'opin-filter-active' : ''}
        key={option}
        onClick={() => {
          onSelect(option);
        }}
        type="button"
      >
        {filterLabel(option)}
      </button>
    ))}
  </nav>
);

export const ProbeMeta = ({
  presentation: { method, methodClass, path },
  status,
}: {
  presentation: ProbePresentation;
  status: string;
}): JSX.Element => (
  <div className="opin-query-meta">
    <span className={methodClass}>{method}</span>
    <code>{path}</code>
    <StatusBadge tone={statusTone(status)}>{statusLabel(status)}</StatusBadge>
  </div>
);

export const ProbeFacts = ({
  scenario,
  presentation: { permissions, scopes },
}: {
  scenario: OpinProbeScenario;
  presentation: ProbePresentation;
}): JSX.Element => (
  <div className="opin-query-facts">
    <span>{`escopos: ${scopes}`}</span>
    <span>{`cenário: ${scenarioLabel(scenario)}`}</span>
    <span>{`permissões: ${permissions}`}</span>
  </div>
);

export const EndpointCatalogButton = ({
  isActive,
  onSelect,
  probe,
}: {
  isActive: boolean;
  onSelect: (probeId: string) => void;
  probe: OpinProbeDefinition;
}): JSX.Element => {
  const presentation = probePresentation(probe);
  return (
    <button
      className={isActive ? 'opin-endpoint-row-active' : ''}
      onClick={() => {
        onSelect(probe.id);
      }}
      type="button"
    >
      <div>
        <span className={presentation.methodClass}>{presentation.method}</span>
        <strong>{probe.label}</strong>
        <code>{presentation.path}</code>
      </div>
      <StatusBadge tone="success">com dados</StatusBadge>
    </button>
  );
};

export const WorkbenchHero = ({
  badge,
  detail,
  title,
}: {
  badge: JSX.Element;
  detail: string;
  title: string;
}): JSX.Element => (
  <div className="opin-workbench-hero">
    <div>
      <span>Teste parecido com o de produção</span>
      <h2>{title}</h2>
      <p>{detail}</p>
    </div>
    {badge}
  </div>
);
